import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEO from '../../../components/SEO/SEO';
import { ArticleSchema, BreadcrumbSchema } from '../../../components/Schema';
import {
    Clock, ArrowLeft, Share2,
    CheckCircle, Copy, BookOpen, ChevronRight,
    Menu, X, ChevronDown, ChevronUp,
    Terminal, PlayCircle, BarChart, Link as LinkIcon, Facebook
} from 'lucide-react';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Progress } from "@/components/ui/progress";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { m, AnimatePresence } from 'framer-motion';
import { tutorials, tutorialCategories, courses } from './CourseData';
import { Course, DetailedCourse, Chapter, Lesson, TutorialCategory } from '../types';

// Import all course content modules as lazy-loaded chunks
const courseModules = import.meta.glob('./content/**/*.{js,ts,tsx}');

// Shimmering Skeleton Loader for better CLS scores
const CourseContentSkeleton = () => (
    <div className="w-full mb-10">
        <div className="mb-[60px] pb-10 border-b-2 border-[#f1f3f4]">
            <Skeleton className="h-6 w-[100px] mb-4 bg-muted local-shimmer" />
            <Skeleton className="h-[48px] w-[80%] mb-6 bg-muted local-shimmer" />
            <Skeleton className="h-[18px] w-full mb-3 bg-muted local-shimmer" />
            <Skeleton className="h-[18px] w-full mb-3 bg-muted local-shimmer" />
        </div>
        <div className="mb-10">
            <Skeleton className="h-[32px] w-[40%] mb-5 bg-muted local-shimmer" />
            <Skeleton className="h-[18px] w-full mb-3 bg-muted local-shimmer" />
            <Skeleton className="h-[18px] w-full mb-3 bg-muted local-shimmer" />
            <Skeleton className="h-[18px] w-full mb-3 bg-muted local-shimmer" />
        </div>
        <div className="mb-10">
            <Skeleton className="h-[24px] w-[30%] mb-4 bg-muted local-shimmer" />
            <Skeleton className="h-[200px] w-full rounded-[12px] my-6 bg-muted local-shimmer" />
        </div>
    </div>
);

const CourseDetailPage: React.FC = () => {
    const { language, slug } = useParams<{ language: string; slug: string }>();
    const [ContentComponent, setContentComponent] = useState<React.ComponentType | null>(null);
    const [htmlContent, setHtmlContent] = useState<string | null>(null);
    const [copied, setCopied] = useState<boolean>(false);

    // Conditional sidebar state: Responsive behavior (Open on Desktop lessons, Hidden on Mobile/Landing)
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(!!slug && (typeof window !== 'undefined' ? window.innerWidth > 1024 : false));
    const [expandedChapters, setExpandedChapters] = useState<string[]>([]);
    const [completedLessons, setCompletedLessons] = useState<string[]>([]);

    // Start as loading if we have a slug to prevent "Empty -> Skeleton" shift
    const [loadingContent, setLoadingContent] = useState(!!slug);

    // HEADER & SCROLL SYNC STATE
    const [scrolled, setScrolled] = useState(false);
    const [headerY, setHeaderY] = useState(0);
    const lastScrollYRef = useRef(0);
    const ticking = useRef(false);
    const [isFabExpanded, setIsFabExpanded] = useState(true);
    const [isAtFooter, setIsAtFooter] = useState(false);
    const footerRef = useRef<HTMLDivElement>(null);

    // Memoize course and lesson to prevent infinite re-renders
    const { course, tutorial, currentLesson } = React.useMemo(() => {
        const c = courses.find((course: DetailedCourse) => course.language === language);
        let t: Course | Lesson | null = null;
        let l: Lesson | null = null;

        if (c && slug) {
            for (const chapter of c.chapters) {
                const lesson = chapter.lessons.find((lesson: Lesson) => lesson.slug === slug);
                if (lesson) {
                    l = lesson;
                    t = {
                        ...lesson,
                        language: c.language,
                        date: 'December 21, 2025',
                        readTime: 10,
                        author: 'FileFusion'
                    } as Course;
                    break;
                }
            }
        }

        // Fallback to tutorials array
        if (!t && slug) {
            t = tutorials.find((tut: Course) => tut.slug === slug && tut.language === language) || null;
        }

        return { course: c, tutorial: t, currentLesson: l };
    }, [language, slug]);

    const category = React.useMemo(() =>
        tutorialCategories.find((c: TutorialCategory) => c.id === language)
        , [language]);

    // Calculate progress
    const allLessons = course?.chapters.flatMap(ch => ch.lessons) || [];
    const progressPercent = allLessons.length > 0 ? Math.round((completedLessons.length / allLessons.length) * 100) : 0;

    // Load completed lessons from localStorage
    useEffect(() => {
        if (course) {
            const saved = localStorage.getItem(`course_progress_${course.language}`);
            if (saved) {
                setCompletedLessons(JSON.parse(saved));
            } else {
                setCompletedLessons([]);
            }
        }
    }, [course?.language]);

    // Save progress when it changes
    useEffect(() => {
        if (course && completedLessons.length > 0) {
            localStorage.setItem(`course_progress_${course.language}`, JSON.stringify(completedLessons));
        }
    }, [completedLessons, course?.language]);

    useEffect(() => {
        if (!slug || !course?.language) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setCompletedLessons(prev => {
                    if (!prev.includes(slug)) {
                        return [...prev, slug];
                    }
                    return prev;
                });
            }
        }, { threshold: 0.1 });

        const sentinel = document.getElementById('lesson-completion-sentinel');
        if (sentinel) observer.observe(sentinel);

        return () => observer.disconnect();
    }, [slug, course?.language]);

    // FAB Footer Detection: Dock when reaching the end of the lesson
    useEffect(() => {
        if (!slug || !course || !footerRef.current) return;

        const observer = new IntersectionObserver((entries) => {
            const isIntersecting = entries[0].isIntersecting;
            setIsAtFooter(isIntersecting);
            if (isIntersecting) {
                setIsFabExpanded(true);
            }
        }, { threshold: 0 });

        observer.observe(footerRef.current);
        return () => observer.disconnect();
    }, [slug, course]);

    useEffect(() => {
        const loadContent = async () => {
            if (tutorial && (tutorial as any).contentFile) {
                const contentFile = (tutorial as any).contentFile;
                const modulePathJS = `./content/${contentFile}.js`;
                const modulePathTS = `./content/${contentFile}.ts`;
                const modulePathTSX = `./content/${contentFile}.tsx`;
                const importFn = courseModules[modulePathJS] || courseModules[modulePathTS] || courseModules[modulePathTSX];

                if (importFn) {
                    setLoadingContent(true);
                    try {
                        const module = await importFn() as { default: any };
                        if (typeof module.default === 'function') {
                            setContentComponent(() => module.default);
                            setHtmlContent(null);
                        } else if (module.default?.content) {
                            setHtmlContent(module.default.content);
                            setContentComponent(null);
                        }
                    } catch (err) {
                        console.error('Failed to load tutorial module:', err);
                    } finally {
                        setLoadingContent(false);
                    }
                }
            } else {
                setContentComponent(null);
                setHtmlContent(null);
            }
        };

        loadContent();
    }, [tutorial?.contentFile, slug]);

    useEffect(() => {
        const handleScrollReset = () => {
            // 1. Force instant jump to top, overriding global smooth scroll
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

            // 2. Synchronize all header and scroll-tracking states
            setScrolled(false);
            setHeaderY(0);
            lastScrollYRef.current = 0;

            // 3. Responsive sidebar behavior: HIDDEN on landing/mobile, OPEN on desktop lessons
            const shouldBeOpen = !!slug && typeof window !== 'undefined' && window.innerWidth > 1024;
            setSidebarOpen(shouldBeOpen);

            // 4. Double-check after a frame to catch any browser scroll restoration/layout races
            requestAnimationFrame(() => {
                if (window.scrollY !== 0) {
                    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                }
            });
        };

        handleScrollReset();

        if (course && slug) {
            const activeChapter = course.chapters.find(ch => ch.lessons.some(l => l.slug === slug));
            if (activeChapter) {
                setExpandedChapters(prev => prev.includes(activeChapter.id) ? prev : [...prev, activeChapter.id]);
            }
        }
    }, [slug, course, language]);

    // SMART HIDE & ELEVATION: Reactive transparency + Scroll Sync logic
    useEffect(() => {
        const updateHeader = () => {
            const currentScrollY = window.scrollY;
            const delta = currentScrollY - lastScrollYRef.current;

            // 1. Handle Elevation (Transparency)
            setScrolled(currentScrollY > 10);

            // 2. Hybrid Scroll-Sync Logic (Antigravity Standard)
            const threshold = 25;
            const targetHeight = 48; // Standard Header Height is 48px

            if (currentScrollY < threshold) {
                // Pin to top at start
                setHeaderY(0);
            } else if (delta > 0) {
                // Scrolling Down: Push away at aggressive 1.8x speed
                if (window.innerWidth >= 1024) { // Only sync in desktop view as per app design
                    setHeaderY(prev => Math.max(-targetHeight, prev - delta * 1.8));
                }
            } else if (delta < 0) {
                // Scrolling Up: Immediate reveal (Antigravity frictionless)
                setHeaderY(0);
            }

            lastScrollYRef.current = currentScrollY;
            ticking.current = false;
        };

        const onScroll = () => {
            const currentScrollY = window.scrollY;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;

            // FAB Logic: "Silent Guard"
            // - Expand at Top (< 50px) for handshake
            // - Expand at Bottom (near footer) for navigation
            // - Shrink everywhere in between to preserve focus
            const isNearTop = currentScrollY < 50;
            const isNearBottom = currentScrollY + windowHeight >= documentHeight - 150;

            if (isNearTop || isNearBottom) {
                setIsFabExpanded(true);
            } else {
                setIsFabExpanded(false);
            }

            if (!ticking.current) {
                window.requestAnimationFrame(updateHeader);
                ticking.current = true;
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, [sidebarOpen]);

    const copyUrl = (e?: React.MouseEvent) => {
        if (e) e.preventDefault();
        const url = window.location.href;
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(url)
                .then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                })
                .catch(() => {
                    fallbackCopyUrl(url);
                });
        } else {
            fallbackCopyUrl(url);
        }
    };

    const fallbackCopyUrl = (text: string) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            document.execCommand('copy');
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Fallback copy failed', err);
        }
        document.body.removeChild(textArea);
    };

    const shareOnFacebook = (e?: React.MouseEvent) => {
        if (e) e.preventDefault();
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank');
    };

    const shareOnX = (e?: React.MouseEvent) => {
        if (e) e.preventDefault();
        const text = slug ? `${tutorial?.title} - ${course?.title}` : `Learn ${category?.name} on FileFusion`;
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(text)}`, '_blank');
    };

    if (!course && !tutorial && !category) {
        return (
            <div className="tutorial-not-found">
                < BookOpen size={64} />
                <h1>Course Content Not Found</h1>
                <p>We couldn't find the course or lesson you're looking for.</p>
                <Link to="/insights/courses" className="back-link">
                    <ArrowLeft size={16} /> Back to Learning Center
                </Link>
            </div>
        );
    }

    const firstLessonSlug = course?.chapters[0]?.lessons[0]?.slug;

    const displayLang = category?.name || (course?.language ? course.language.charAt(0).toUpperCase() + course.language.slice(1) : '');
    const seoTitle = slug
        ? `${tutorial?.title} | ${displayLang} Course | FileFusion`
        : `${course?.title || category?.name} | FileFusion`;

    return (
        <div className="flex flex-col min-h-screen bg-white font-brand">
            <SEO
                key={slug || 'course-overview'}
                title={seoTitle}
                description={tutorial?.excerpt || course?.description || `Learn ${category?.name} in our comprehensive course.`}
                keywords={tutorial?.keywords?.join(', ') || [category?.name, 'course', 'learning'].join(', ')}
                type={slug ? "article" : "course"}
            />





            {/* Ghost Sidebar Toggle (Appears when scrolled past breadcrumbs) */}
            <AnimatePresence>
                {!sidebarOpen && scrolled && (
                    <m.button
                        initial={{ opacity: 0, y: -20, scale: 0.8 }}
                        animate={{ opacity: 0.4, y: 0, scale: 1 }}
                        whileHover={{ opacity: 1, scale: 1.05 }}
                        exit={{ opacity: 0, y: -10, scale: 0.9 }}
                        className="fixed left-2 top-[72px] z-[90] ghost-sidebar-toggle items-center justify-center bg-none border-none p-0 cursor-pointer text-[#1a73e8] transition-opacity duration-200"
                        onClick={() => setSidebarOpen(true)}
                        title="Show Sidebar"
                    >
                        <div className="flex items-center font-mono font-bold text-lg h-[44px]">
                            <span>{">"}</span>
                            <span className="animate-pulse">_</span>
                        </div>
                    </m.button>
                )}
            </AnimatePresence>

            {/* Thumb-Friendly Curriculum FAB (Mobile Only) */}
            <AnimatePresence>
                {!sidebarOpen && slug && (
                    <m.button
                        initial={{ opacity: 0, scale: 0.8, y: 20 }}
                        animate={{ opacity: isAtFooter ? 0 : 1, scale: isAtFooter ? 0.8 : 1, y: isAtFooter ? 20 : 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: 20 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setSidebarOpen(true)}
                        className={`fixed bottom-6 right-6 z-[100] flex items-center bg-[#1a73e8]/90 backdrop-blur-md text-white rounded-full shadow-[0_8px_32px_rgba(26,115,232,0.3)] border border-white/20 cursor-pointer transition-all duration-500 hover:bg-[#1a73e8] curriculum-fab ring-1 ring-white/10 overflow-hidden pointer-events-auto
                            ${isAtFooter ? 'pointer-events-none' : ''}
                            ${isFabExpanded ? 'px-6 py-3.5' : 'p-3.5'}
                        `}
                        aria-label="Toggle curriculum menu"
                    >
                        <BookOpen size={20} className="shrink-0" />
                        <AnimatePresence>
                            {isFabExpanded && (
                                <m.span
                                    initial={{ width: 0, opacity: 0, marginLeft: 0 }}
                                    animate={{ width: 'auto', opacity: 1, marginLeft: 8 }}
                                    exit={{ width: 0, opacity: 0, marginLeft: 0 }}
                                    transition={{ duration: 0.3, ease: "easeOut" }}
                                    className="font-medium text-[0.9375rem] whitespace-nowrap overflow-hidden"
                                >
                                    Curriculum
                                </m.span>
                            )}
                        </AnimatePresence>
                    </m.button>
                )}
            </AnimatePresence>

            {/* Overlay for Mobile Sidebar */}
            {sidebarOpen && <div className="lg:hidden fixed inset-0 bg-black/40 z-[1000] backdrop-blur-[2px]" onClick={() => setSidebarOpen(false)} />}

            <div className="flex flex-col lg:flex-row flex-1 w-full">
                {/* Course Sidebar */}
                <m.aside
                    layout="position"
                    animate={{
                        top: typeof window !== 'undefined' && window.innerWidth < 1024 ? 0 : 48 + headerY,
                        height: typeof window !== 'undefined' && window.innerWidth < 1024 ? '100dvh' : `calc(100vh - ${48 + headerY}px)`
                    }}
                    transition={{
                        duration: 0.15,
                        ease: "easeOut"
                    }}
                    className={`fixed lg:sticky left-0 bottom-0 bg-white z-[1001] lg:z-10 flex flex-col shadow-[16px_0_24px_rgba(0,0,0,0.05)] lg:shadow-none overflow-hidden
                        ${sidebarOpen ? 'translate-x-0 opacity-100 visible w-[280px] lg:w-[240px]' : '-translate-x-full lg:w-0 lg:opacity-0 lg:invisible w-0'}
                    `}
                >
                    <div className="p-6 shrink-0 relative">
                        <div className="flex items-center gap-2 mb-6 relative">
                            <button
                                onClick={() => setSidebarOpen(false)}
                                className="flex items-center gap-1 group transition-colors text-[#1a73e8] hover:text-[#174ea6] bg-none border-none p-0 -ml-4 cursor-pointer"
                                title="Hide Sidebar"
                            >
                                <div className="flex items-center font-mono font-bold text-lg">
                                    <span>{"<"}</span>
                                    <span>_</span>
                                </div>
                            </button>
                            <Typography variant="h4" className="text-[1.125rem] font-semibold text-[#202124] m-0">Course Outline</Typography>
                        </div>

                        <div className="mt-4">
                            <div className="flex justify-between text-[0.75rem] text-[#5f6368]/80 mb-2 font-medium uppercase tracking-wider">
                                <span>Your Progress</span>
                                <span>{progressPercent}%</span>
                            </div>
                            <Progress value={progressPercent} className="h-1 bg-[#e8f0fe] [&>div]:bg-[#1a73e8]" />
                        </div>
                        {/* Cinematic Fade Line */}
                        <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#dadce0] to-transparent opacity-60" />
                    </div>

                    <ScrollArea className="flex-1 min-h-0 [&>[data-slot=scroll-area-viewport]]:overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>[data-slot=scroll-area-scrollbar]]:hidden">
                        <div className="py-4 pb-[env(safe-area-inset-bottom,20px)]">
                            <Accordion type="multiple" value={expandedChapters} onValueChange={setExpandedChapters} className="w-full">
                                {course?.chapters.map((chapter: Chapter, idx: number) => (
                                    <m.div
                                        key={chapter.id}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: idx * 0.05 + 0.1, duration: 0.3 }}
                                    >
                                        <AccordionItem value={chapter.id} className="border-none mb-1">
                                            <AccordionTrigger className="relative w-full overflow-hidden flex items-center pl-4 pr-12 py-3 hover:bg-[#f8f9fa] transition-colors duration-200 no-underline hover:no-underline [&>svg]:absolute [&>svg]:right-1 [&>svg]:!size-4 [&>svg]:!text-[#5f6368]/80">
                                                <div className="flex items-center flex-1 text-left min-w-0">
                                                    <span className="text-[0.675rem] font-bold text-[#5f6368]/60 w-4 shrink-0">{idx + 1}</span>
                                                    <span className="text-[0.82rem] font-medium text-[#202124] mx-2 tracking-tight leading-snug">
                                                        {chapter.title.replace(/^\d+\.\s*/, '')}
                                                    </span>
                                                </div>
                                            </AccordionTrigger>
                                            <AccordionContent className="p-0 bg-white">
                                                <div className="py-1.5 w-full overflow-hidden">
                                                    {chapter.lessons.map((lesson: Lesson) => (
                                                        <Link
                                                            key={lesson.id}
                                                            to={`/insights/courses/${language}/${lesson.slug}`}
                                                            className={`flex items-start pl-4 pr-10 py-[8px] no-underline text-[0.79rem] transition-all duration-200 hover:text-[#1a73e8] ${slug === lesson.slug ? 'text-[#1a73e8] font-medium' : 'text-[#5f6368]'}`}
                                                            onClick={() => window.innerWidth <= 1024 && setSidebarOpen(false)}
                                                        >
                                                            <div className="mr-2.5 flex items-center shrink-0 pt-1">
                                                                {completedLessons.includes(lesson.slug) ? (
                                                                    <CheckCircle size={13} className="text-[#34a853]" />
                                                                ) : slug === lesson.slug ? (
                                                                    <PlayCircle size={13} />
                                                                ) : (
                                                                    <div className={`w-1 h-1 bg-[#dadce0] rounded-full ${slug === lesson.slug ? 'bg-[#1a73e8]' : ''}`} />
                                                                )}
                                                            </div>
                                                            <span className="flex-1 leading-snug">{lesson.title}</span>
                                                        </Link>
                                                    ))}
                                                </div>
                                            </AccordionContent>
                                        </AccordionItem>
                                    </m.div>
                                ))}
                            </Accordion>
                        </div>
                    </ScrollArea>
                </m.aside>

                <main className="flex-1 flex flex-col w-full min-w-0 overflow-x-hidden">
                    <div
                        className={`hidden lg:flex pt-6 pb-4 justify-center items-center bg-white w-full 
                            ${scrolled ? 'shadow-sm' : ''}
                        `}
                    >
                        <div className="w-full px-6 transition-all duration-300 flex justify-between items-center max-w-full min-h-[44px]">
                            <div className="flex items-center gap-3 text-[0.875rem] text-[#5f6368] flex-1 min-w-0 overflow-hidden">
                                {!sidebarOpen && (
                                    <button
                                        onClick={() => setSidebarOpen(true)}
                                        className="flex items-center gap-1 group transition-colors text-[#1a73e8] hover:text-[#174ea6] bg-none border-none p-0 -ml-4 cursor-pointer"
                                        title="Show Sidebar"
                                    >
                                        <div className="flex items-center font-mono font-bold text-lg">
                                            <span>{">"}</span>
                                            <span className="animate-pulse">_</span>
                                        </div>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={`flex-1 px-4 xs:px-6 py-6 md:px-8 md:py-8 ${sidebarOpen ? 'lg:px-12' : 'lg:px-20'} lg:pt-8 lg:pb-0 bg-white w-full lg:items-center flex flex-col min-h-[80vh]`}>
                        {/* Shimmer Keyframes defined locally to avoid global index.css changes */}
                        <style>{`
                            @keyframes shimmer {
                                0% { background-position: -1000px 0; }
                                100% { background-position: 1000px 0; }
                            }
                            .local-shimmer {
                                background: linear-gradient(to right, #f6f7f8 4%, #edeef1 25%, #f6f7f8 36%);
                                background-size: 1000px 100%;
                                animation: shimmer 2s infinite linear;
                            }
                            @media (max-width: 1024px) {
                                .ghost-sidebar-toggle {
                                    display: none !important;
                                }
                            }
                            @media (min-width: 1024px) {
                                .curriculum-fab {
                                    display: none !important;
                                }
                            }
                        `}</style>

                        <AnimatePresence mode="wait">
                            <m.div
                                layout
                                key={slug || 'course-overview'}
                                initial={{ opacity: 0, y: 15 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -15 }}
                                transition={{ duration: 0.4, ease: [0.33, 1, 0.68, 1] }}
                                className="w-full"
                            >
                                {slug ? (
                                    <article className={`focus:outline-none transition-all duration-300 font-brand w-full max-w-[1200px]
                                                [&_h1]:text-[2.25rem] [&_h1]:font-light [&_h1]:text-[#202124] [&_h1]:mb-6 [&_h1]:leading-[1.2] [&_h1]:tracking-tight
                                                [&_h2]:text-[1.75rem] [&_h2]:font-light [&_h2]:text-[#202124] [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:leading-[1.3] [&_h2]:tracking-tight
                                                [&_h3]:text-[1.35rem] [&_h3]:font-light [&_h3]:text-[#202124] [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:tracking-tight
                                                [&_p]:text-[1.125rem] [&_p]:leading-[1.75] [&_p]:text-[#3c4043] [&_p]:mb-5
                                                [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ul]:text-[#3c4043]
                                                [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_ol]:text-[#3c4043]
                                                [&_li]:mb-2 [&_li]:leading-[1.6]
                                                [&_table]:w-full [&_table]:border-collapse [&_table]:my-8 [&_table]:text-[0.9375rem]
                                                [&_th]:bg-[#f8f9fa] [&_th]:p-3 [&_th]:border [&_th]:border-[#dadce0] [&_th]:text-left [&_th]:font-medium [&_th]:text-[#202124]
                                                [&_td]:p-3 [&_td]:border [&_td]:border-[#dadce0] [&_td]:text-[#5f6368]
                                                [&_blockquote]:border-l-4 [&_blockquote]:border-[#1a73e8] [&_blockquote]:bg-[#f8f9fa] [&_blockquote]:p-6 [&_blockquote]:my-8 [&_blockquote]:italic [&_blockquote]:text-[#202124]
                                                [&_code]:bg-[#f1f3f4] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[#d93025] [&_code]:text-[0.9em] [&_code]:font-mono
                                                [&_pre]:bg-[#202124] [&_pre]:p-5 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:my-6
                                                [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-[#e8f0fe] [&_pre_code]:text-[0.875rem]
                                                [&_.info-box]:bg-[#e8f0fe] [&_.info-box]:p-6 [&_.info-box]:rounded-xl [&_.info-box]:border-l-4 [&_.info-box]:border-[#1a73e8] [&_.info-box]:my-8
                                                [&_.info-box_h4]:text-[#174ea6] [&_.info-box_h4]:mt-0 [&_.info-box_h4]:mb-2 [&_.info-box_h4]:font-medium
                                                [&_.info-box_p]:text-[#174ea6] [&_.info-box_p]:m-0 [&_.info-box_p]:text-[1rem]
                                                [&_.use-case-box]:bg-[#f8f9fa] [&_.use-case-box]:p-8 [&_.use-case-box]:rounded-2xl [&_.use-case-box]:border [&_.use-case-box]:border-[#dadce0] [&_.use-case-box]:my-10
                                                [&_.use-case-box_h3]:mt-0 [&_.use-case-box_h3]:text-[#1a73e8]
                                                [&_.use-case-list]:list-none [&_.use-case-list]:p-0 [&_.use-case-list]:mt-6
                                                [&_.use-case-item]:flex [&_.use-case-item]:gap-4 [&_.use-case-item]:mb-4 [&_.use-case-item:before]:text-[#5f6368]
                                                [&_.use-case-item:before]:content-['✓'] [&_.use-case-item:before]:text-[#34a853] [&_.use-case-item:before]:font-bold
                                            `}>
                                        {loadingContent ? (
                                            <CourseContentSkeleton />
                                        ) : ContentComponent ? (
                                            <ContentComponent />
                                        ) : (
                                            <div className="mb-6 leading-[1.75] text-[1.125rem]" dangerouslySetInnerHTML={{ __html: htmlContent || '' }} />
                                        )}

                                        {/* Updated Navigation Footer */}
                                        {(() => {
                                            if (!course || !currentLesson) return null;

                                            const allCourseLessons = course.chapters.flatMap(ch => ch.lessons);
                                            const currentIndex = allCourseLessons.findIndex(l => l.slug === currentLesson.slug);

                                            const prevLesson = currentIndex > 0 ? allCourseLessons[currentIndex - 1] : null;
                                            const nextLesson = currentIndex < allCourseLessons.length - 1 ? allCourseLessons[currentIndex + 1] : null;

                                            return (
                                                <div ref={footerRef} className="mt-8 border-t border-[#dadce0]">
                                                    <div className="flex justify-between items-center p-3">
                                                        {prevLesson ? (
                                                            <Button asChild variant="outline" className="h-10 px-6 rounded-full text-[#3c4043] hover:bg-[#f8fafc] hover:shadow-sm active:bg-[#f1f3f4]">
                                                                <Link to={`/insights/courses/${language}/${prevLesson.slug}`} className="flex items-center gap-2 no-underline">
                                                                    <ArrowLeft size={18} />
                                                                    <span>Previous</span>
                                                                </Link>
                                                            </Button>
                                                        ) : (
                                                            <div />
                                                        )}

                                                        {nextLesson && (
                                                            <Button asChild variant="outline" className="h-10 px-6 rounded-full text-[#3c4043] hover:bg-[#f8fafc] hover:shadow-sm active:bg-[#f1f3f4]">
                                                                <Link to={`/insights/courses/${language}/${nextLesson.slug}`} className="flex items-center gap-2 no-underline">
                                                                    <span>Next</span>
                                                                    <ChevronRight size={18} />
                                                                </Link>
                                                            </Button>
                                                        )}
                                                    </div>

                                                    {/* Mobile Docked Curriculum Button */}
                                                    <div className="md:hidden flex justify-center pb-6">
                                                        <m.button
                                                            initial={{ opacity: 0, y: 10 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            onClick={() => setSidebarOpen(true)}
                                                            className="flex items-center gap-2 px-6 py-3.5 bg-[#1a73e8] text-white rounded-full shadow-md hover:bg-[#1967d2] active:scale-95 transition-all text-[0.9375rem] font-medium"
                                                        >
                                                            <BookOpen size={20} />
                                                            Curriculum
                                                        </m.button>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </article>
                                ) : (
                                    <div className={`w-full mx-auto pb-[60px] transition-all duration-300 ${sidebarOpen ? 'max-w-[860px]' : 'max-w-[1240px]'}`}>
                                        <div className="py-5 pb-[60px] border-b border-[#dadce0] mb-[60px] text-center flex flex-col items-center">
                                            <div className="inline-block px-3 py-1.5 bg-[#e8f0fe] text-[#174ea6] rounded-[4px] text-[0.75rem] font-bold uppercase tracking-[0.5px] mb-6">{category?.name} Course</div>

                                            <Typography variant="hero-title" as="h1" weight="light" className="mb-6 text-[#202124]">
                                                {course?.title || `${category?.name} Mastery`}
                                            </Typography>

                                            <Typography variant="hero-subtitle" className="mb-10 mx-auto">
                                                {course?.description || category?.description || `Master ${category?.name} with our professional, step-by-step curriculum designed for both beginners and advanced developers.`}
                                            </Typography>

                                            <div className="flex gap-8 mb-12 justify-center flex-wrap">
                                                <div className="flex items-center gap-2 text-[#5f6368]/80 text-[0.9375rem]">
                                                    <BookOpen size={20} className="text-[#1a73e8]" />
                                                    <span>{allLessons.length} lessons</span>
                                                </div>
                                                <div className="flex items-center gap-2 text-[#5f6368]/80 text-[0.9375rem]">
                                                    <Clock size={20} className="text-[#1a73e8]" />
                                                    <span>Approx. 4 hours</span>
                                                </div>
                                                <div className="flex items-center gap-2 text-[#5f6368]/80 text-[0.9375rem]">
                                                    <BarChart size={20} className="text-[#1a73e8]" />
                                                    <span>All Levels</span>
                                                </div>
                                            </div>

                                            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                                                <Button asChild className="h-auto px-8 py-4 bg-[#1a73e8] text-white rounded-full font-medium text-[1.125rem] hover:bg-[#1967d2] hover:-translate-y-[1px] shadow-md hover:shadow-lg transition-all duration-200">
                                                    <Link to={`/insights/courses/${language}/${firstLessonSlug}`} className="flex items-center justify-center gap-3 no-underline">
                                                        Start Learning Now
                                                    </Link>
                                                </Button>

                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button
                                                            variant="outline"
                                                            className="rounded-full px-7 py-3.5 h-14 bg-transparent border-[#dadce0] text-[#202124] font-medium text-[1rem] flex items-center justify-center gap-2.5 transition-all duration-200 hover:bg-[#f8f9fa] hover:border-[#1a73e8] hover:text-[#1a73e8] data-[state=open]:bg-[#f8f9fa] data-[state=open]:border-[#1a73e8] data-[state=open]:text-[#1a73e8]"
                                                        >
                                                            <Share2 size={18} /> Share Course
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="min-w-[220px] p-2 rounded-[12px] shadow-[0_10px_30px_rgba(0,0,0,0.12)] border-[#dadce0]">
                                                        <DropdownMenuItem onClick={copyUrl} className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-[#5f6368] text-[0.875rem] font-medium focus:bg-[#f1f3f4] focus:text-[#1a73e8] cursor-pointer">
                                                            {copied ? <CheckCircle size={16} className="text-[#34a853]" /> : <LinkIcon size={16} />}
                                                            {copied ? 'Link Copied' : 'Copy Link'}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={shareOnX} className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-[#5f6368] text-[0.875rem] font-medium focus:bg-[#f1f3f4] focus:text-[#1a73e8] cursor-pointer">
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                                            </svg>
                                                            Share on X
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={shareOnFacebook} className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-[#5f6368] text-[0.875rem] font-medium focus:bg-[#f1f3f4] focus:text-[#1a73e8] cursor-pointer">
                                                            <Facebook size={16} fill="currentColor" />
                                                            Facebook
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-[60px] text-left">
                                            <section>
                                                <Typography variant="article-title" weight="light" className="mb-8">What you'll learn</Typography>
                                                <ul className="list-none p-0 flex flex-col gap-5">
                                                    <li className="flex gap-4 text-[1.0625rem] text-[#5f6368] leading-[1.5]"><CheckCircle size={18} className="text-[#34a853] shrink-0 mt-0.5" /> Professional development environments and system setup</li>
                                                    <li className="flex gap-4 text-[1.0625rem] text-[#5f6368] leading-[1.5]"><CheckCircle size={18} className="text-[#34a853] shrink-0 mt-0.5" /> Fundamental syntax, core data structures, and best practices</li>
                                                    <li className="flex gap-4 text-[1.0625rem] text-[#5f6368] leading-[1.5]"><CheckCircle size={18} className="text-[#34a853] shrink-0 mt-0.5" /> Modern architectural patterns and advanced core features</li>
                                                    <li className="flex gap-4 text-[1.0625rem] text-[#5f6368] leading-[1.5]"><CheckCircle size={18} className="text-[#34a853] shrink-0 mt-0.5" /> Hands-on projects and professional tooling integration</li>
                                                </ul>
                                            </section>

                                            <section className="bg-[#f8f9fa] p-8 rounded-[12px] h-fit">
                                                <Typography variant="article-title" weight="light" className="mb-8">Course Curriculum</Typography>
                                                <div className="flex flex-col gap-6">
                                                    {course?.chapters.slice(0, 3).map((chapter: Chapter) => (
                                                        <div key={chapter.id}>
                                                            <h3 className="text-[1rem] font-medium text-[#202124] mb-1">{chapter.title}</h3>
                                                            <p className="text-[0.875rem] text-[#5f6368]/60 m-0">{chapter.lessons.length} lessons</p>
                                                        </div>
                                                    ))}
                                                    {course?.chapters && course.chapters.length > 3 && (
                                                        <p className="text-[0.9375rem] text-[#1a73e8] font-medium mt-2">...and {course.chapters.length - 3} more chapters</p>
                                                    )}
                                                </div>
                                            </section>
                                        </div>
                                    </div>
                                )}
                            </m.div>
                        </AnimatePresence>
                    </div>
                </main>
            </div>



            <div id="lesson-completion-sentinel" style={{ height: '1px' }} />
        </div>
    );
};

export default CourseDetailPage;
